import React, { useEffect, useRef, useState } from "react";
import { getQualityGrid, getQualitySites, createQualityControl, uploadFile, fileUrl } from "../../api";
import SignatureCanvas from "../collab/SignaturePad";
import { Camera, Loader2, MapPin, Plus, Star, Trash2, CheckCircle2, X } from "lucide-react";
import { log } from "../../lib/log";

const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand bg-white";

const Stars = ({ value, onChange, testid }) => (
  <div className="flex gap-0.5 relative z-10 shrink-0" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(value === n ? 0 : n)} aria-label={`${n} sur 5`} data-testid={`${testid}-${n}`}
        className={`p-1.5 -m-0.5 rounded-md ${n <= value ? "text-amber-500" : "text-neutral-300 hover:text-amber-300"}`}><Star size={24} fill={n <= value ? "currentColor" : "none"} /></button>
    ))}
  </div>
);

const pct = (zones) => { const n = zones.filter((z) => z.score); return n.length ? Math.round(n.reduce((s, z) => s + (z.score - 1) / 4, 0) / n.length * 100) : 0; };

/* Shared quality-control form (admin tab + mobile majors). `requireGps` forces an on-site position before submit. */
const scoreCls = (score) => {
  if (score >= 85) return "text-emerald-600";
  if (score >= 65) return "text-amber-600";
  return "text-red-600";
};
const gpsLabel = (gps, requireGps) => {
  if (gps) return "Position confirmée";
  return requireGps ? "Confirmer ma position (obligatoire)" : "Confirmer ma position";
};

const QualityForm = ({ onDone, onCancel, requireGps = false, initialSite = null }) => {
  const [grid, setGrid] = useState([]);
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState(initialSite || { client_nom: "", adresse: "", ville: "", socid: null, client_email: "" });
  const [zones, setZones] = useState([]);
  const [points, setPoints] = useState("");
  const newAction = () => ({ key: Math.random().toString(36).slice(2, 9), label: "", echeance: "" });
  const [actions, setActions] = useState(() => [newAction()]);
  const [photos, setPhotos] = useState([]);
  const [visa, setVisa] = useState("");
  const [hasSig, setHasSig] = useState(false);
  const [gps, setGps] = useState(null);
  const [sendReport, setSendReport] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sigRef = useRef(null);

  useEffect(() => {
    getQualityGrid().then((r) => { setGrid(r.data.zones); setZones(r.data.zones.map((z) => ({ key: z.key, score: 0, comment: "" }))); }).catch(log.error);
    getQualitySites().then((r) => setSites(r.data.sites)).catch(log.error);
  }, []);

  const pickSite = (name) => { const s = sites.find((x) => x.client_nom === name); setSite(s ? { ...s } : { ...site, client_nom: name }); };
  const setZone = (i, patch) => setZones((zs) => zs.map((z, j) => (j === i ? { ...z, ...patch } : z)));
  const locate = () => navigator.geolocation?.getCurrentPosition((p) => setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }), () => setErr("Position GPS indisponible"), { enableHighAccuracy: true, timeout: 12000 });
  const addPhotos = async (files) => {
    for (const f of Array.from(files)) {
      try { const r = await uploadFile(f); setPhotos((p) => [...p, r.data.path]); } catch (e) { log.error(e); setErr("Photo non envoyée"); }
    }
  };
  const uploadSignature = () => new Promise((res) => sigRef.current.toBlob(async (blob) => {
    try { const r = await uploadFile(new File([blob], "visa.png", { type: "image/png" })); res(r.data.path); } catch (e) { log.error(e); res(null); }
  }, "image/png"));

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!site.client_nom.trim()) return setErr("Indiquez le client / site");
    if (!zones.some((z) => z.score)) return setErr("Notez au moins une zone");
    if (requireGps && !gps) return setErr("Activez votre position GPS pour valider le contrôle sur site");
    setBusy(true);
    try {
      const signature = hasSig ? await uploadSignature() : null;
      const { data } = await createQualityControl({ ...site, zones, points_a_corriger: points, actions: actions.map(({ key, ...a }) => a), photos, signature, client_visa_nom: visa, gps, send_report: sendReport });
      onDone(data);
    } catch (ex) { setErr(ex?.response?.data?.detail || "Enregistrement impossible"); } finally { setBusy(false); }
  };

  const score = pct(zones);
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="quality-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Client / site *</label>
          <input list="quality-sites" className={inputCls} value={site.client_nom} onChange={(e) => pickSite(e.target.value)} placeholder="Choisir ou saisir un client" data-testid="quality-client" />
          <datalist id="quality-sites">{sites.map((s) => <option key={s.client_nom} value={s.client_nom} />)}</datalist>
        </div>
        <input className={inputCls} placeholder="Adresse" value={site.adresse || ""} onChange={(e) => setSite({ ...site, adresse: e.target.value })} data-testid="quality-adresse" />
        <input className={inputCls} placeholder="Ville" value={site.ville || ""} onChange={(e) => setSite({ ...site, ville: e.target.value })} data-testid="quality-ville" />
      </div>

      <div className="space-y-3" data-testid="quality-zones">
        {grid.map((z, i) => (
          <div key={z.key} className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-neutral-800">{z.label}</p>
              <Stars value={zones[i]?.score || 0} onChange={(v) => setZone(i, { score: v })} testid={`quality-zone-${z.key}`} />
            </div>
            <input className={`${inputCls} mt-2`} placeholder="Commentaire (facultatif)" value={zones[i]?.comment || ""} onChange={(e) => setZone(i, { comment: e.target.value })} data-testid={`quality-zone-${z.key}-comment`} />
          </div>
        ))}
      </div>
      <p className="text-sm font-semibold text-neutral-800" data-testid="quality-live-score">Conformité globale : <span className={scoreCls(score)}>{score} %</span></p>

      <textarea rows={3} className={inputCls} placeholder="Points à corriger" value={points} onChange={(e) => setPoints(e.target.value)} data-testid="quality-points" />

      <div>
        <p className="text-xs font-semibold text-neutral-600 mb-1">Actions correctives à planifier</p>
        {actions.map((a, i) => (
          <div key={a.key} className="flex gap-2 mb-2">
            <input className={inputCls} placeholder="Action" value={a.label} onChange={(e) => setActions(actions.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`quality-action-${i}`} />
            <input type="date" className={`${inputCls} w-40`} value={a.echeance} onChange={(e) => setActions(actions.map((x, j) => (j === i ? { ...x, echeance: e.target.value } : x)))} data-testid={`quality-action-${i}-date`} />
            <button type="button" onClick={() => setActions(actions.filter((_, j) => j !== i))} className="text-neutral-400 hover:text-red-500"><Trash2 size={15} /></button>
          </div>
        ))}
        <button type="button" onClick={() => setActions([...actions, newAction()])} className="text-xs text-brand font-semibold flex items-center gap-1" data-testid="quality-add-action"><Plus size={12} /> Ajouter une action</button>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-700 cursor-pointer bg-neutral-100 hover:bg-neutral-200 px-3 py-2 rounded-md">
          <Camera size={15} /> Ajouter des photos<input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} data-testid="quality-photos-input" />
        </label>
        {photos.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-testid="quality-photos">{photos.map((p) => <div key={p} className="relative"><img src={fileUrl(p)} alt="" className="w-20 h-20 object-cover rounded-md" /><button type="button" onClick={() => setPhotos(photos.filter((x) => x !== p))} className="absolute -top-1.5 -right-1.5 bg-white rounded-full shadow p-0.5"><X size={12} /></button></div>)}</div>}
      </div>

      <div>
        <p className="text-xs font-semibold text-neutral-600 mb-1">Visa du client sur place (facultatif)</p>
        <input className={`${inputCls} mb-2`} placeholder="Nom du signataire" value={visa} onChange={(e) => setVisa(e.target.value)} data-testid="quality-visa-nom" />
        <SignatureCanvas ref={sigRef} onDraw={setHasSig} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={locate} className={`text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1 ${gps ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"}`} data-testid="quality-gps">
          {gps ? <CheckCircle2 size={13} /> : <MapPin size={13} />} {gpsLabel(gps, requireGps)}</button>
        <label className="flex items-center gap-2 text-sm text-neutral-700"><input type="checkbox" className="accent-brand w-4 h-4" checked={sendReport} onChange={(e) => setSendReport(e.target.checked)} data-testid="quality-send-report" /> Envoyer le rapport PDF au client</label>
        {sendReport && <input type="email" className={`${inputCls} sm:w-64`} placeholder="Email du client" value={site.client_email || ""} onChange={(e) => setSite({ ...site, client_email: e.target.value })} data-testid="quality-client-email" />}
      </div>

      {err && <p className="text-sm text-red-600" data-testid="quality-error">{err}</p>}
      <div className="flex gap-2 justify-end">
        {onCancel && <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md border border-neutral-300 text-sm hover:bg-neutral-50" data-testid="quality-cancel">Annuler</button>}
        <button type="submit" disabled={busy} className="bg-brand hover:bg-brand-dark text-white font-semibold px-5 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60" data-testid="quality-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Valider le contrôle</button>
      </div>
    </form>
  );
};

export default QualityForm;
